import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns';
import {
  DateMaximumFeePeriod,
  DateRatePeriod,
  MaximumFeeDetail,
  RateDetail,
  RegularRule,
  SpecificRule,
  WeeklyMaximumFeePeriod,
  WeeklyRatePeriod,
} from '../models/ParkingRate.ts';
import {
  mergeAdjacentBlocks,
  uid,
  PALETTE,
  SPEC_COLORS,
  type RateGroup,
  type TimeBlock,
} from '../hooks/parking-services/useParkingRateGroups.ts';
import {
  DOW_ORDER,
  fmt,
  fmtEnd,
  timeToSlot,
  computeDowSubset,
} from './ParkingRateTime.ts';

// ─── MaxFee draft state types ───────────────────────────────────────────────────
// 前端編輯器的 draft state 型別（非後端 API DTO），與 RateGroup 一樣屬於編輯器 domain。

export interface DraftFeeItem {
  mode: 'WHOLE_DAY' | 'FIRST_UNIT' | 'PER_UNIT' | '';
  fee: string;
  interval: string;
}

export interface MaxFeeTimeSlotEntry {
  id: number;
  startTime: string;
  endTime: string;
}

export interface MaxFeeRuleEntry {
  id: number;
  /** regular: DOW indices (0=Mon…6=Sun); specific: date indices into specialDates */
  checkedDays: number[];
  daySlots: Record<number, MaxFeeTimeSlotEntry[]>;
  feeItems: DraftFeeItem[];
}

/** 是否為合法的 "HH:mm"（00:00–23:59）。編輯時段輸入框時，值可能是尚未 commit
 *  的原始字串（如 "0930"、"9"），需先過濾避免組出無效的 datetime。 */
const isValidHHmm = (t: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);

// ─── Rule period domain logic ───────────────────────────────────────────────────

export const REGULAR_MAX_END = '2099-12-31';

/** 給定一般費率的起始日與其他既有規則，回傳不與任何人重疊的最長結束日。 */
export function computeRegularEndDate(
  startStr: string,
  otherRules: RegularRule[],
): string {
  const laterStarts = otherRules
    .map((r) => r.effectivePeriod.startDateTime.split('T')[0])
    .filter((s) => s > startStr)
    .sort();
  if (laterStarts.length === 0) return REGULAR_MAX_END;
  return format(subDays(parseISO(laterStarts[0]), 1), 'yyyy-MM-dd');
}

/**
 * 更新 rule 的結束日並過濾掉新期間內不存在的星期 ratePeriods。
 * 當新 endStr 使得有效期間 < 7 天時，ratePeriods 內包含「不在該區間」的 DOW
 * 的 time slot 必須移除，否則後端會回 INVALID_INPUT。
 */
export function filterRuleToEffectivePeriod(
  rule: RegularRule,
  newEndStr: string,
): RegularRule {
  const startStr = rule.effectivePeriod.startDateTime.split('T')[0];
  const dowSubset = computeDowSubset(startStr, newEndStr);
  const updatedRule: RegularRule = {
    ...rule,
    effectivePeriod: {
      ...rule.effectivePeriod,
      endDateTime: `${newEndStr}T23:59:59`,
    },
  };
  if (dowSubset.length === 7) return updatedRule;
  const allowedDows = new Set<string>(dowSubset.map((i) => DOW_ORDER[i]));
  return {
    ...updatedRule,
    ratePeriods: rule.ratePeriods
      .map((period) => ({
        ...period,
        dayOfWeekTimeSlots: period.dayOfWeekTimeSlots.filter((slot) =>
          allowedDows.has(slot.startDayOfWeek),
        ),
      }))
      .filter((period) => period.dayOfWeekTimeSlots.length > 0),
  };
}

/** 判斷兩個日期區間 [aStart, aEnd] 與 [bStart, bEnd] 是否重疊（含邊界）。
 *  "YYYY-MM-DD" 格式可直接做字典序比較。 */
export function periodsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

// ─── Rate converters ────────────────────────────────────────────────────────────

export function regularRuleToGroups(
  rule: RegularRule,
  dowSubset: number[] = [0, 1, 2, 3, 4, 5, 6],
): RateGroup<RateDetail>[] {
  // DOW 全域索引 (0=Mon…6=Sun) → 欄位索引（在 dowSubset 中的位置）
  const dowToCol = new Map<number, number>(
    dowSubset.map((dow, col) => [dow, col]),
  );
  return rule.ratePeriods.map((period, periodIdx) => {
    const color = PALETTE[periodIdx % PALETTE.length];
    const blockMap = new Map<string, Set<number>>();
    period.dayOfWeekTimeSlots.forEach((slot) => {
      const key = `${slot.startTime}-${slot.endTime}`;
      if (!blockMap.has(key)) blockMap.set(key, new Set());
      const di = DOW_ORDER.indexOf(slot.startDayOfWeek);
      const colIdx = dowToCol.get(di);
      if (colIdx !== undefined) blockMap.get(key)!.add(colIdx);
    });
    const blocks: TimeBlock[] = [...blockMap.entries()].map(([key, days]) => {
      const [startTime, endTime] = key.split('-');
      return {
        id: uid(),
        days: [...days].sort((a, b) => a - b),
        start: timeToSlot(startTime),
        end: timeToSlot(endTime) - 1,
      };
    });
    return {
      id: uid(),
      color,
      rate: period.rate,
      blocks: mergeAdjacentBlocks(blocks),
    };
  });
}

export function groupsToRegularRatePeriods(
  groups: RateGroup[],
  dowSubset: number[] = [0, 1, 2, 3, 4, 5, 6],
): WeeklyRatePeriod[] {
  return groups
    .filter((g) => g.rate !== null)
    .map((g) => ({
      dayOfWeekTimeSlots: g.blocks.flatMap((b) =>
        b.days.map((d) => ({
          startDayOfWeek: DOW_ORDER[dowSubset[d]],
          startTime: fmt(b.start),
          endDayOfWeek: DOW_ORDER[dowSubset[d]],
          endTime: fmtEnd(b.end + 1),
        })),
      ),
      rate: g.rate,
    }));
}

export function specificRuleToGroups(
  rule: SpecificRule,
): RateGroup<RateDetail>[] {
  const startDateStr = rule.effectivePeriod.startDateTime.split('T')[0];
  return rule.ratePeriods.map((period, periodIdx) => {
    const color = SPEC_COLORS[periodIdx % SPEC_COLORS.length];
    const blockMap = new Map<string, Set<number>>();
    period.dateTimeSlots.forEach((slot) => {
      const slotDate = slot.startDateTime.split('T')[0];
      const dayIdx = differenceInCalendarDays(
        parseISO(slotDate),
        parseISO(startDateStr),
      );
      const startTime = format(parseISO(slot.startDateTime), 'HH:mm');
      const endTime = format(parseISO(slot.endDateTime), 'HH:mm');
      const key = `${startTime}-${endTime}`;
      if (!blockMap.has(key)) blockMap.set(key, new Set());
      blockMap.get(key)!.add(dayIdx);
    });
    const blocks: TimeBlock[] = [...blockMap.entries()].map(([key, days]) => {
      const [startTime, endTime] = key.split('-');
      return {
        id: uid(),
        days: [...days].sort((a, b) => a - b),
        start: timeToSlot(startTime),
        end: timeToSlot(endTime) - 1,
      };
    });
    return {
      id: uid(),
      color,
      rate: period.rate,
      blocks: mergeAdjacentBlocks(blocks),
    };
  });
}

export function groupsToSpecificRatePeriods(
  groups: RateGroup<RateDetail>[],
  dates: string[],
): DateRatePeriod[] {
  return groups
    .filter((g) => g.rate !== null)
    .map((g) => ({
      dateTimeSlots: g.blocks.flatMap((b) =>
        b.days
          .filter((d) => d < dates.length)
          .map((dayIdx) => ({
            startDateTime: `${dates[dayIdx]}T${fmt(b.start)}:00`,
            endDateTime: `${dates[dayIdx]}T${fmtEnd(b.end + 1)}:00`,
          })),
      ),
      rate: g.rate,
    }));
}

// ─── MaxFeeEntry converters ─────────────────────────────────────────────────────

export function draftFeeItemsToMaxFeeDetails(
  items: DraftFeeItem[],
): MaximumFeeDetail[] {
  return items
    .filter((item) => item.mode !== '')
    .map((item) => {
      if (item.mode === 'WHOLE_DAY')
        return { type: 'WHOLE_DAY', fee: parseInt(item.fee) || 0 };
      if (item.mode === 'FIRST_UNIT')
        return {
          type: 'FIRST_UNIT',
          fee: parseInt(item.fee) || 0,
          intervalMinutes: parseInt(item.interval) || 0,
        };
      return {
        type: 'PER_UNIT',
        fee: parseInt(item.fee) || 0,
        intervalMinutes: parseInt(item.interval) || 0,
      };
    });
}

export function regularRuleToMaxFeeEntries(
  rule: RegularRule,
): MaxFeeRuleEntry[] {
  return rule.maximumFeeRules.map((period) => {
    const daySlots: Record<number, MaxFeeTimeSlotEntry[]> = {};
    period.dayOfWeekTimeSlots.forEach((slot) => {
      const dayIdx = DOW_ORDER.indexOf(slot.startDayOfWeek);
      if (!daySlots[dayIdx]) daySlots[dayIdx] = [];
      daySlots[dayIdx].push({
        id: uid(),
        startTime: slot.startTime.slice(0, 5),
        endTime: slot.endTime.slice(0, 5),
      });
    });
    const checkedDays = Object.keys(daySlots)
      .map(Number)
      .sort((a, b) => a - b);
    return {
      id: uid(),
      checkedDays,
      daySlots,
      feeItems: period.maximumFee.map((f) => ({
        mode: f.type,
        fee: String(f.fee),
        interval: f.type !== 'WHOLE_DAY' ? String(f.intervalMinutes) : '',
      })),
    };
  });
}

export function specificRuleToMaxFeeEntries(
  rule: SpecificRule,
  dates: string[],
): MaxFeeRuleEntry[] {
  const dateIndexMap = new Map(dates.map((d, i) => [d, i]));
  return rule.maximumFeeRules.map((period) => {
    const daySlots: Record<number, MaxFeeTimeSlotEntry[]> = {};
    period.dateTimeSlots.forEach((slot) => {
      const dateStr = slot.startDateTime.split('T')[0];
      const dateIdx = dateIndexMap.get(dateStr);
      if (dateIdx === undefined) return;
      if (!daySlots[dateIdx]) daySlots[dateIdx] = [];
      daySlots[dateIdx].push({
        id: uid(),
        startTime: format(parseISO(slot.startDateTime), 'HH:mm'),
        endTime: format(parseISO(slot.endDateTime), 'HH:mm'),
      });
    });
    const checkedDays = Object.keys(daySlots)
      .map(Number)
      .sort((a, b) => a - b);
    return {
      id: uid(),
      checkedDays,
      daySlots,
      feeItems: period.maximumFee.map((f) => ({
        mode: f.type,
        fee: String(f.fee),
        interval: f.type !== 'WHOLE_DAY' ? String(f.intervalMinutes) : '',
      })),
    };
  });
}

export function maxFeeEntriesToRegularPeriods(
  entries: MaxFeeRuleEntry[],
): WeeklyMaximumFeePeriod[] {
  return entries
    .filter((e) => e.checkedDays.length > 0 && e.feeItems.length > 0)
    .map((e) => ({
      dayOfWeekTimeSlots: e.checkedDays.flatMap((dayIdx) => {
        const slots = e.daySlots[dayIdx];
        if (!slots || slots.length === 0)
          return [
            {
              startDayOfWeek: DOW_ORDER[dayIdx],
              startTime: '00:00:00',
              endDayOfWeek: DOW_ORDER[dayIdx],
              endTime: '23:59:00',
            },
          ];
        return slots.flatMap((s) => {
          if (!isValidHHmm(s.startTime) || !isValidHHmm(s.endTime)) return [];
          // 跨日（end < start）不截斷、不拆兩段，原樣存成同一天的 start/end。
          return [
            {
              startDayOfWeek: DOW_ORDER[dayIdx],
              startTime: s.startTime + ':00',
              endDayOfWeek: DOW_ORDER[dayIdx],
              endTime: s.endTime + ':00',
            },
          ];
        });
      }),
      maximumFee: draftFeeItemsToMaxFeeDetails(e.feeItems),
    }));
}

export function maxFeeEntriesToSpecificPeriods(
  entries: MaxFeeRuleEntry[],
  dates: string[],
): DateMaximumFeePeriod[] {
  return entries
    .filter((e) => e.checkedDays.length > 0 && e.feeItems.length > 0)
    .map((e) => ({
      dateTimeSlots: e.checkedDays
        .filter((idx) => idx < dates.length)
        .flatMap((dateIdx) => {
          const slots = e.daySlots[dateIdx];
          if (!slots || slots.length === 0)
            return [
              {
                startDateTime: `${dates[dateIdx]}T00:00:00`,
                endDateTime: `${dates[dateIdx]}T23:59:00`,
              },
            ];
          return slots.flatMap((s) => {
            if (!isValidHHmm(s.startTime) || !isValidHHmm(s.endTime)) return [];
            // 跨日（end < start）不截斷、不拆兩段，原樣存成同一天的 start/end。
            return [
              {
                startDateTime: `${dates[dateIdx]}T${s.startTime}:00`,
                endDateTime: `${dates[dateIdx]}T${s.endTime}:00`,
              },
            ];
          });
        }),
      maximumFee: draftFeeItemsToMaxFeeDetails(e.feeItems),
    }));
}
